<template>
  <div>
    <div class="mb-3">
      <label for="">{{ 'Header' | translate }}</label>
      <input class="form-control" type="text" v-model="block.header" :placeholder="$t('Header')" maxlength="200" />
    </div>

    <hr />

    <div class="mb-3">
      <div class="mb-3">
        <label class="">{{ $t('CTA') }}</label>
        <input class="form-control" v-model="buttonText" :placeholder="$t('Subscribe')" maxlength="100" />
      </div>
      <div class="mb-3">
        <label class="">{{ $t('Success header') }}</label>
        <input class="form-control" v-model="successHeader" maxlength="100" />
        <small class="text-muted">{{ 'Shows after the form is submitted' | translate }}.</small>
      </div>
      <div class="mb-3">
        <label class="">{{ $t('Success text') }}</label>
        <input class="form-control" v-model="successText" maxlength="200" />
        <small class="text-muted">{{ 'Shows after the form is submitted' | translate }}.</small>
      </div>
    </div>

    <hr class="my-5" />

    <div class="mb-3">
      <div class="form-check mb-3">
        <input class="form-check-input" type="checkbox" v-model="marketing" id="marketing-field" />
        <label class="form-check-label" for="marketing-field">
          {{ $t('Optional marketing consent checkbox') }}
        </label>
      </div>
      <div class="mb-3" v-if="marketing">
        <label class="">{{ $t('Checkbox text') }}</label>
        <input class="form-control" v-model="marketingLabel" />
      </div>
    </div>

    <hr class="my-5" />

    <div class="mb-3">
      <div v-for="(field, idx) in hiddenFields" :key="`field_${field.name}`" class="card mb-2">
        <div class="card-header d-flex justify-content-between">
          <i class="fas fa-bars grabbable p-1"></i>
          <button
            class="btn btn-outline-danger btn-sm"
            type="button"
            @click="removeHiddenField(idx)"
          >
            {{ 'Delete' | translate }}
          </button>
        </div>
        <div class="card-body small font-monospace">
          <div>{{ 'Field name' | translate }}: {{ field.name }}</div>
          <div>{{ 'Type' | translate }}: {{ field.type }}</div>
          <div>{{ 'Value' | translate }}: 1</div>
        </div>
      </div>
    </div>

    <div class="mb-3">
      <draggable :list="fields" handle=".grabbable">
        <div v-for="(field, idx) in fields" :key="`field_${field.name}`" class="card mb-2">
          <div class="card-header d-flex justify-content-between">
            <i class="fas fa-bars grabbable p-1"></i>
            <button
              class="btn btn-outline-danger btn-sm"
              type="button"
              @click="removeField(idx)"
              v-if="!['name', 'email'].includes(field.name)"
            >
              {{ 'Delete' | translate }}
            </button>
          </div>
          <div class="card-body small font-monospace">
            <div>{{ 'Field name' | translate }}: {{ field.name }}</div>
            <div>{{ 'Label' | translate }}: {{ field.label }}</div>
            <div>{{ 'Placeholder' | translate }}: {{ field.placeholder }}</div>
            <div>{{ 'Type' | translate }}: {{ field.type }}</div>
            <div>{{ 'Required' | translate }}: {{ field.required }}</div>
          </div>
        </div>
      </draggable>

      <div v-if="showNewFieldForm">
        <hr />
        <div class="mb-3">
          <label class="">{{ $t('Field name') }}</label>
          <TheMask
            class="form-control"
            v-model="newField.name"
            :placeholder="$t('field_name')"
            required
            maxlength="50"
            :mask="'FFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF'"
            :tokens="{
              F: { pattern: /[a-z0-9_]/ },
            }"
          ></TheMask>
          <small class="text-muted">{{ 'Field identifier used in integration' | translate }}.</small>
        </div>
        <div class="mb-3" v-if="newField.type !== 'hidden'">
          <label class="">{{ $t('Label') }}</label>
          <input class="form-control" v-model="newField.label" />
        </div>
        <div class="mb-3" v-if="newField.type === 'hidden'">
          <label class="">{{ $t('Value') }}</label>
          <input class="form-control" value="1" disabled />
        </div>
        <div class="mb-3" v-if="!['select', 'checkbox', 'hidden'].includes(newField.type)">
          <label class="">{{ $t('Placeholder') }}</label>
          <input class="form-control" v-model="newField.placeholder" maxlength="100" />
        </div>
        <div class="mb-3">
          <label class="">{{ $t('Type') }}</label>
          <select class="form-select" v-model="newField.type" required>
            <option value="text">{{ $t('text') }}</option>
            <option value="email">{{ $t('email') }}</option>
            <option value="textarea">{{ $t('text area') }}</option>
            <option value="checkbox">{{ $t('single-choice') }} (checkbox)</option>
            <option value="select">{{ $t('single-choice') }} (select)</option>
            <option value="hidden">{{ $t('hidden') }}</option>
          </select>
        </div>
        <div class="mb-3" v-if="newField.type === 'select'">
          <label class="">{{ $t('Options') }}</label>
          <div class="input-group mb-2">
            <input class="form-control" v-model="optionTextInput" :placeholder="$t('Label')" maxlength="100" />
            <input class="form-control" v-model="optionValueInput" :placeholder="$t('Value')" maxlength="100" />
            <button class="btn btn-outline-secondary" type="button" @click="addOption">
              {{ $t('Add') }}
            </button>
          </div>
          <ul class="list-group">
            <li
              v-for="(opt, idx) in newField.options"
              :key="idx"
              class="list-group-item d-flex justify-content-between align-items-center"
            >
              <span>
                <b>{{ opt.text }}</b>
                <span v-if="opt.value !== opt.text" class="text-muted ms-2">({{ opt.value }})</span>
              </span>
              <button class="btn btn-outline-danger btn-sm" type="button" @click="removeOption(idx)">
                {{ 'Delete' | translate }}
              </button>
            </li>
          </ul>
        </div>
        <div class="" v-if="newField.type !== 'hidden'">
          <div class="form-check mt-4">
            <input class="form-check-input" type="checkbox" v-model="newField.required" id="requiredField" />
            <label class="form-check-label" for="requiredField">
              {{ $t('Required') }}
            </label>
          </div>
        </div>
        <button class="btn btn-primary mt-3" @click="addField" :disabled="!newField.name">
          {{ 'Add' | translate }}
        </button>
      </div>
    </div>

    <button v-if="!showNewFieldForm" class="btn btn-primary" @click="showNewFieldForm = true">
      {{ 'Add' | translate }}
    </button>

    <div v-if="validationErrors.length" class="mt-3">
      <div v-for="(error, index) in validationErrors" :key="index" class="alert alert-danger">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import draggable from 'vuedraggable'
import { TheMask } from 'vue-the-mask'

export default {
  components: {
    TheMask,
    draggable,
  },
  props: ['block'],
  data: () => ({
    showNewFieldForm: false,
    newField: {
      required: false,
      name: '',
      label: '',
      placeholder: '',
      type: 'text',
      options: [],
    },
    optionTextInput: '',
    optionValueInput: '',
    validationErrors: [],
  }),
  mounted() {
    this.setHiddenFields()
    this.ensureRequiredFields()
  },
  methods: {
    setHiddenFields() {
      const settings = _.get(this.block, 'settings', {})
      this.$set(this.block, 'settings', { ...settings, hidden_fields: this.hiddenFields })
    },
    resetNewField() {
      this.showNewFieldForm = false
      this.newField = {
        required: false,
        name: '',
        label: '',
        placeholder: '',
        type: 'text',
        options: [],
      }
      this.optionTextInput = ''
      this.optionValueInput = ''
      this.validationErrors = []
    },
    validate(fields, newField) {
      const errors = []
      if (!newField.name || (typeof newField.name === 'string' && newField.name.trim() === '')) {
        errors.push(this.$t('Field name is required'))
      }
      if (newField.name && fields.some((f) => f.name && f.name.toLowerCase() === newField.name.toLowerCase())) {
        errors.push(this.$t('Field name must be unique'))
      }
      if (!newField.type) {
        errors.push(this.$t('Field type is required'))
      }
      return errors
    },
    getCleanedField() {
      const cleaned = _.pickBy(
        this.newField,
        (v) => !(typeof v === 'string' && v.trim() === '') && !(Array.isArray(v) && v.length === 0),
      )

      if (cleaned.type === 'hidden') {
        delete cleaned.placeholder
        delete cleaned.label
        delete cleaned.required
        cleaned.value = 1
      }

      if (cleaned.name) {
        cleaned.name = _.deburr(_.snakeCase(cleaned.name))
      }
      return cleaned
    },
    addField() {
      const cleanedField = this.getCleanedField()
      const errors = this.validate(this.fields, cleanedField)
      if (errors.length) {
        this.validationErrors = errors
        return
      }
      if (cleanedField.type === 'hidden') {
        this.hiddenFields = [...this.hiddenFields, cleanedField]
      } else {
        this.fields = [...this.fields, cleanedField]
      }
      this.resetNewField()
    },
    addOption() {
      const trimmedText = this.optionTextInput.trim()
      const trimmedValue = this.optionValueInput.trim()
      if (trimmedText && !this.newField.options.some((opt) => opt.text === trimmedText)) {
        this.newField.options.push({
          value: trimmedValue || trimmedText,
          text: trimmedText,
        })
        this.optionTextInput = ''
        this.optionValueInput = ''
      }
    },
    removeOption(index) {
      this.newField.options.splice(index, 1)
    },
    removeField(index) {
      const field = this.fields[index]
      if (['name', 'email'].includes(field.name)) return
      this.fields.splice(index, 1)
    },
    removeHiddenField(index) {
      const field = this.fields[index]
      this.hiddenFields.splice(index, 1)
    },
    ensureRequiredFields() {
      const required = [
        { name: 'name', label: this.$t('Name'), type: 'text', required: true },
        { name: 'email', label: this.$t('Email'), type: 'email', required: true },
      ]
      required.forEach((req) => {
        if (!this.fields.some((f) => f.name === req.name)) {
          this.fields = [...this.fields, req]
        }
      })
    },
  },
  computed: {
    hiddenFields: {
      get() {
        return _.get(this.block, 'settings.hidden_fields', [])
      },
      set(value) {
        const settings = _.get(this.block, 'settings', {})
        this.$set(this.block, 'settings', { ...settings, hidden_fields: value })
      },
    },
    product() {
      return _.get(this, '$parent.$parent.$parent.courseId', null)
    },
    marketing: {
      get() {
        return _.get(this.block, 'settings.marketing', false)
      },
      set(value) {
        const settings = _.get(this.block, 'settings', {})
        this.$set(this.block, 'settings', { ...settings, marketing: value })
      },
    },
    marketingLabel: {
      get() {
        return _.get(this.block, 'settings.marketing_label', '')
      },
      set(value) {
        const settings = _.get(this.block, 'settings', {})
        this.$set(this.block, 'settings', { ...settings, marketing_label: value })
      },
    },
    buttonText: {
      get() {
        return _.get(this.block, 'settings.button_text', '')
      },
      set(value) {
        const settings = _.get(this.block, 'settings', {})
        this.$set(this.block, 'settings', { ...settings, button_text: value })
      },
    },
    successHeader: {
      get() {
        return _.get(this.block, 'settings.success_header', '')
      },
      set(value) {
        const settings = _.get(this.block, 'settings', {})
        this.$set(this.block, 'settings', { ...settings, success_header: value })
      },
    },
    successText: {
      get() {
        return _.get(this.block, 'settings.success_text', '')
      },
      set(value) {
        const settings = _.get(this.block, 'settings', {})
        this.$set(this.block, 'settings', { ...settings, success_text: value })
      },
    },
    fields: {
      get() {
        return _.get(this.block, 'settings.fields', [])
      },
      set(value) {
        const settings = _.get(this.block, 'settings', {})
        this.$set(this.block, 'settings', { ...settings, fields: value })
      },
    },
  },
}
</script>
